import React, { useEffect, useState } from "react";
import { auth } from "../lib/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { getUserRole, getUserPages, getAllPages, createPage, deletePage, getPendingRequests, approveUser, denyUser, getAllApprovedUsers, setUserRole } from "../lib/config";
import { useNavigate } from "react-router-dom";

function Dashboard() {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null); // { role, status }
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newPageId, setNewPageId] = useState("");
    const [error, setError] = useState(null);
    const [creating, setCreating] = useState(false);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [approvedUsers, setApprovedUsers] = useState([]);
    const navigate = useNavigate();

    const isAdminUser = userRole?.role === "admin";

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (u) => {
            if (!u) {
                navigate("/");
                return;
            }
            const role = await getUserRole(u.uid);
            if (!role || role.status !== "approved") {
                navigate("/");
                return;
            }
            setUser(u);
            setUserRole(role);
            loadPages(u.uid, role.role === "admin");
            if (role.role === "admin") {
                loadPendingRequests();
                loadApprovedUsers();
            }
        });
        return unsub;
    }, [navigate]);

    async function loadPages(uid, admin) {
        setLoading(true);
        try {
            const userPages = admin ? await getAllPages() : await getUserPages(uid);
            setPages(userPages);
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    }

    async function loadPendingRequests() {
        try {
            const reqs = await getPendingRequests();
            setPendingRequests(reqs);
        } catch (err) { /* silent */ }
    }

    async function loadApprovedUsers() {
        try {
            const users = await getAllApprovedUsers();
            setApprovedUsers(users);
        } catch (err) { /* silent */ }
    }

    async function handleCreate() {
        if (!newPageId.trim()) return;
        if (!/^[a-zA-Z0-9-]+$/.test(newPageId.trim())) {
            setError("Page ID can only contain letters, numbers, and hyphens.");
            return;
        }
        setCreating(true);
        setError(null);
        try {
            await createPage(newPageId.trim(), user.uid);
            setNewPageId("");
            await loadPages(user.uid, isAdminUser);
        } catch (err) {
            setError(err.message);
        }
        setCreating(false);
    }

    async function handleDelete(pageId) {
        if (!window.confirm(`Delete page "${pageId}"? This cannot be undone.`)) return;
        try {
            await deletePage(pageId);
            await loadPages(user.uid, isAdminUser);
        } catch (err) {
            setError(err.message);
        }
    }

    async function handleApprove(uid) {
        try {
            await approveUser(uid);
            setPendingRequests(prev => prev.filter(r => r.uid !== uid));
            await loadApprovedUsers();
        } catch (err) { /* silent */ }
    }

    async function handleDeny(uid) {
        if (!window.confirm("Deny and remove this access request?")) return;
        try {
            await denyUser(uid);
            setPendingRequests(prev => prev.filter(r => r.uid !== uid));
        } catch (err) { /* silent */ }
    }

    async function handleToggleAdmin(uid, currentRole) {
        const newRole = currentRole === "admin" ? "user" : "admin";
        const msg = newRole === "admin" ? "Grant admin access?" : "Revoke admin access?";
        if (!window.confirm(msg)) return;
        try {
            await setUserRole(uid, newRole);
            setApprovedUsers(prev => prev.map(u => u.uid === uid ? { ...u, role: newRole } : u));
        } catch (err) { /* silent */ }
    }

    async function handleRemoveUser(uid) {
        if (!window.confirm("Remove this user's access?")) return;
        try {
            await denyUser(uid);
            setApprovedUsers(prev => prev.filter(u => u.uid !== uid));
        } catch (err) { /* silent */ }
    }

    if (!user) return null;

    return (
        <div className="dashboard-page">
            <div className="dashboard-header">
                <h1>🚇 MTA Time Dashboard</h1>
                <div className="dashboard-user">
                    <span>{user.displayName} {isAdminUser && <span style={{ background: '#2563eb', color: '#fff', padding: '2px 8px', borderRadius: 6, fontSize: '0.75rem', marginLeft: 6 }}>Admin</span>}</span>
                    <button className="text-btn" onClick={() => signOut(auth)}>Sign out</button>
                </div>
            </div>

            <div className="dashboard-content">
                {/* Admin: Pending requests */}
                {isAdminUser && pendingRequests.length > 0 && (
                    <div className="settings-section" style={{ background: '#fef3cd', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
                        <h2 style={{ marginBottom: 12 }}>🔔 Pending Access Requests ({pendingRequests.length})</h2>
                        {pendingRequests.map(req => (
                            <div key={req.uid} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                background: '#fff', borderRadius: 8, padding: '10px 16px', marginBottom: 8,
                            }}>
                                <div>
                                    <strong>{req.displayName || "Unknown"}</strong>
                                    <span style={{ color: '#666', marginLeft: 8, fontSize: '0.85rem' }}>{req.email}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button className="primary-btn" style={{ padding: '6px 14px', fontSize: '0.85rem' }} onClick={() => handleApprove(req.uid)}>
                                        ✓ Approve
                                    </button>
                                    <button className="danger-btn" style={{ padding: '6px 14px', fontSize: '0.85rem' }} onClick={() => handleDeny(req.uid)}>
                                        ✕ Deny
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}


                <div className="create-page-card">
                    <h2>Create New Page</h2>
                    <div className="create-page-form">
                        <span className="create-page-prefix">mtatime.pages.dev /</span>
                        <input
                            type="text"
                            placeholder="e.g. 8419"
                            value={newPageId}
                            onChange={(e) => setNewPageId(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                            className="create-page-input"
                        />
                        <button
                            className="primary-btn"
                            onClick={handleCreate}
                            disabled={creating || !newPageId.trim()}
                        >
                            {creating ? "Creating..." : "Create"}
                        </button>
                    </div>
                    {error && <p className="error-text">{error}</p>}
                </div>

                <h2>{isAdminUser ? "All Pages" : "Your Pages"}</h2>
                {loading ? (
                    <p className="loading-text">Loading pages...</p>
                ) : pages.length === 0 ? (
                    <p className="empty-text">No pages yet. Create one above!</p>
                ) : (
                    <div className="pages-grid">
                        {pages.map((page) => (
                            <div key={page.id} className="page-card">
                                <div className="page-card-header">
                                    <h3>/{page.id}</h3>
                                    {isAdminUser && page.ownerId !== user.uid && (
                                        <span style={{ fontSize: '0.75rem', color: '#888' }}>
                                            Owner: {page.ownerId?.slice(0, 8)}...
                                        </span>
                                    )}
                                </div>
                                <p className="page-card-detail">
                                    Stop: {page.subwayStopId || "—"} · Routes: {(page.subwayRoutes || []).map(r => r.routeId).join(", ") || "—"}
                                </p>
                                <div className="page-card-actions">
                                    <button className="primary-btn" onClick={() => navigate(`/${page.id}/edit`)}>
                                        ⚙️ Edit
                                    </button>
                                    <button className="secondary-btn" onClick={() => window.open(`/${page.id}`, "_blank")}>
                                        👁️ View
                                    </button>
                                    <button className="danger-btn" onClick={() => handleDelete(page.id)}>
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Admin: User management */}
                {isAdminUser && approvedUsers.length > 0 && (
                    <div style={{ background: '#f0f9ff', borderRadius: 12, padding: '16px 20px', marginTop: 24 }}>
                        <h2 style={{ marginBottom: 12 }}>👥 Users ({approvedUsers.filter(u => u.uid !== user.uid).length})</h2>
                        {approvedUsers.filter(u => u.uid !== user.uid).map(u => (
                            <div key={u.uid} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                background: '#fff', borderRadius: 8, padding: '10px 16px', marginBottom: 8,
                            }}>
                                <div>
                                    <strong>{u.displayName || "Unknown"}</strong>
                                    <span style={{ color: '#666', marginLeft: 8, fontSize: '0.85rem' }}>{u.email}</span>
                                    {u.role === "admin" && (
                                        <span style={{ background: '#2563eb', color: '#fff', padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', marginLeft: 8 }}>Admin</span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                        className={u.role === "admin" ? "secondary-btn" : "primary-btn"}
                                        style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                                        onClick={() => handleToggleAdmin(u.uid, u.role)}
                                    >
                                        {u.role === "admin" ? "Revoke Admin" : "Make Admin"}
                                    </button>
                                    <button className="danger-btn" style={{ padding: '6px 14px', fontSize: '0.85rem' }} onClick={() => handleRemoveUser(u.uid)}>
                                        ✕ Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Dashboard;
